import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormGroup,
  Paper,
  IconButton,
  Tooltip,
  Menu,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { getSuiteById } from "../../features/slices/Suite";
import { useNotification } from "../../context/NotificationContext";
import { useLoader } from "../../context/LoaderContext";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  BarChart,
  LineChart,
  PieChart,
  pieArcLabelClasses,
} from "@mui/x-charts";
import moment from "moment";
import { AppDispatch } from "../../store";
import { Case } from "../../interfaces/Case";

interface Suite {
  cases: Case[];
  title: string;
}

const SuiteStat: React.FC = () => {
  const { suitId } = useParams<{ suitId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { showLoader, hideLoader } = useLoader();
  const [suite, setSuite] = useState<Suite | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<
    Record<string, boolean>
  >({});
  const [chartType, setChartType] = useState<string>("Bar");
  const chartRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  useEffect(() => {
    const fetchSuite = async () => {
      showLoader();
      try {
        const result = await dispatch(getSuiteById(Number(suitId))).unwrap();
        const initialStatuses: Record<string, boolean> = {};
        result.cases.forEach((caseItem: Case) => {
          initialStatuses[caseItem.tc_result] = true;
        });
        setSelectedStatuses(initialStatuses);
        setSuite(result);
      } catch (error) {
        notify("Failed to fetch suite", "error");
      } finally {
        hideLoader();
      }
    };

    fetchSuite();
  }, []);

  const copyAll = () => {
    const text = Object.keys(selectedStatuses)
      .map(
        (status) =>
          `${status}:${
            suite?.cases.filter((caseItem) => caseItem.tc_result === status)
              .length
          }`
      )
      .join("\n");
    navigator.clipboard.writeText(text).then(() => {
      notify("Copied all statuses to clipboard", "success");
    });
  };
  const handleStatusChange = (status: string) => {
    setSelectedStatuses((prev) => ({
      ...prev,
      [status]: !prev[status],
    }));
  };

  const handleChartTypeChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setChartType(event.target.value as string);
  };
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      notify("Copied to clipboard", "success");
    });
  };
  const handleExportClick = (event: {
    currentTarget: React.SetStateAction<null>;
  }) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const exportChart = async (format: string) => {
    const canvas = await html2canvas(chartRef.current);
    const image = canvas.toDataURL(`image/${format}`);
    const link = document.createElement("a");
    link.href = image;
    const currentDate = moment().format("YYYY-MM-DD");
    link.download = `chart_suit${suitId}_${currentDate}.${format}`;
    link.click();
    handleClose();
  };

  const resetFilters = () => {
    const resetStatuses: Record<string, boolean> = {};
    suite?.cases.forEach((caseItem: Case) => {
      resetStatuses[caseItem.tc_result] = true;
    });
    setSelectedStatuses(resetStatuses);
    setChartType("Bar");
  };

  const renderChart = () => {
    if (!suite || !suite.cases.length) {
      return <Typography>No data available for chart.</Typography>;
    }

    const filteredData = suite.cases.filter(
      (caseItem: Case) => selectedStatuses[caseItem.tc_result]
    );
    if (!filteredData.length) {
      return <Typography>No data available for selected statuses.</Typography>;
    }
    const statusCounts = filteredData.reduce((acc, caseItem) => {
      if (!acc[caseItem.tc_result]) {
        acc[caseItem.tc_result] = { count: 0, lastExecutedAt: new Date(0) };
      }

      acc[caseItem.tc_result].count += 1;

      acc[caseItem.tc_result].lastExecutedAt = new Date(caseItem.executed_at);

      return acc;
    }, {});

    const statusColors: Record<string, string> = {
      PASSED: "#4CAF50",
      FAILED: "#FF9800",
      FAILED_ENSURE: "#FF9800",
      CMD_ERROR: "#E53935",
      INTERNAL_ERROR: "#E53935",
      RESERVED: "gray",
      INIT: "#9E9E9E",
    };

    const chartData = Object.entries(statusCounts).map(([status, info]) => ({
      color: statusColors[status],
      name: status,
      label: status,
      data: [info.count],
      count: info.count,
      time: info.lastExecutedAt,
    }));

    if (!chartData.length) {
      return (
        <Typography>No chart data available for current selection.</Typography>
      );
    }
    const valueFormatter = (value: number) => `${value} cases`;

    switch (chartType) {
      case "Bar":
        return (
          <BarChart
            series={chartData.map((series) => ({
              ...series,
              label: series.name,
              type: "bar",
              layout: "vertical",
              valueFormatter,
            }))}
            xAxis={[{ scaleType: "band", data: ["Statuses"] }]}
            barLabel={(item) => {
              return item.value?.toString();
            }}
            colors={chartData.map((series) => series.color)}
            grid={{ horizontal: true }}
            height={400}
          />
        );
      case "Pie":
        return (
          <PieChart
            series={[
              {
                data: chartData.map((item) => ({
                  id: item.name,
                  value: item.data[0],
                  label: item.name,
                  color: item.color,
                })),
                arcLabel: (item) => item.value,
                highlightScope: { faded: "global", highlighted: "item" },
                faded: {
                  innerRadius: 30,
                  additionalRadius: -30,
                  color: "gray",
                },
              },
            ]}
            sx={{
              [`& .${pieArcLabelClasses.root}`]: {
                fill: "white",
                fontSize: 12,
                fontWeight: "bold",
              },
            }}
            height={400}
          />
        );
      default:
        return null;
    }
  };

  const renderCheckboxes = () =>
    Object.keys(selectedStatuses).map((status) => {
      const count =
        suite?.cases.filter((caseItem) => caseItem.tc_result === status)
          .length || 0;
      const label = `${status}:${count}`;

      return (
        <Box
          key={status}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedStatuses[status]}
                onChange={() => handleStatusChange(status)}
              />
            }
            label={label}
          />
          <Tooltip title="Copy to Clipboard">
            <IconButton onClick={() => copyToClipboard(label)} size="small">
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    });

  return (
    <Paper
      elevation={4}
      sx={{
        p: 3,
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box
        sx={{ display: "flex", flex: 1, gap: "10px", flexDirection: "column" }}
      >
        <Button onClick={() => navigate(-1)} variant="text" sx={{ alignSelf: "flex-start" }}>
          ← Back
        </Button>
        <Button onClick={resetFilters} variant="outlined">
          Reset Filters
        </Button>
        <Typography variant="h6" gutterBottom>
          Filter by Status:
        </Typography>
        <FormGroup>{renderCheckboxes()}</FormGroup>
        <Button onClick={copyAll}>Copy all</Button>
      </Box>
      <Box sx={{ flex: 2 }}>
        <FormControl sx={{ ml: 2, width: "200px" }}>
          <InputLabel id="chart-type-select-label">Chart Type</InputLabel>
          <Select
            labelId="chart-type-select-label"
            id="chart-type-select"
            value={chartType}
            label="Chart Type"
            onChange={handleChartTypeChange}
          >
            <MenuItem value="Bar">Bar</MenuItem>
            {/* <MenuItem value="Line">Line</MenuItem> */}
            <MenuItem value="Pie">Pie</MenuItem>
          </Select>
          <Button
            aria-controls="simple-menu"
            aria-haspopup="true"
            onClick={handleExportClick}
            variant="contained"
          >
            Export Chart
          </Button>
          <Menu
            id="simple-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={() => exportChart("png")}>
              Export as PNG
            </MenuItem>
            <MenuItem onClick={() => exportChart("jpeg")}>
              Export as JPEG
            </MenuItem>
          </Menu>
        </FormControl>
        <div ref={chartRef} id="chartRef">
          {renderChart()}
        </div>
      </Box>
    </Paper>
  );
};

export default SuiteStat;
